import { useSelector } from 'react-redux';
import { BackgroundImage, Image, withContent } from '@bubbletea/core';
import { currencyFormatter, getPageUrl } from '@mooncake/utils';
import { useTheme } from 'styled-components';
import { IContent } from '../../../types';
import { IMainTheme } from '../../../types/theme';
import { HowToItem } from '../../../types/generatedContent';
import { IStoreState } from '../../../types/store';

import {
  PushContainer,
  PushImage,
  PushDetails,
  PushContent,
  PushTitle,
  PushName,
  PushPrice,
  PushActions,
  PriceContainer,
  DiscountedPrice,
  BasePrice,
  SalePill,
} from './styles';
import Button from '../../UI/Button';
import Loader from '../../../core/UI/Loader';
import sendEvent from '../../../utils/sendEvent';

interface IPushProps {
  context: IContent;
  item: HowToItem;
  index: number;
  isProductInStock: boolean;
}

const Push = ({ context, item, index, isProductInStock }: IPushProps) => {
  const theme = useTheme() as IMainTheme;
  const contentSelector = useSelector((state: IStoreState) => ({
    products: state.products.items,
    currency: state.coreApp.currency,
    locale: state.coreApp.locale,
  }));
  const { currency, locale, products } = contentSelector;

  const { blockTitle, blockPrice, blockImage, blockProductId, blockAppStore, blockGooglePlay } = item;
  const { global } = context.googlesheet;
  const { getItNow, sale } = global;

  const productFound = blockProductId && products ? products.filter((product) => product.id === blockProductId) : [];
  let productPrice = null;

  if (productFound.length === 1) {
    const promotionFound =
      productFound[0].product_promotions?.length && productFound[0].product_promotions[0]?.promotional_price;
    productPrice = {
      basePrice: productFound[0].price as number,
      discountedPrice: promotionFound,
    };
  }

  const goToProduct = () => {
    const productPageLink = getPageUrl('product', blockProductId);
    if (productPageLink) {
      const eventDetails = {
        location: 'how to connect my guitar or bass',
        locationDetail: 'tone cable : available',
        category: 'action',
        locationPosition: process.env['REACT_APP_PAGE_TYPE'] === 'subscriber' ? 2 : 8,
        action: 'tone cable : see more',
      };
      sendEvent(eventDetails);
      window.open(productPageLink, '_blank');
    }
  };

  const sendAppStoreTracking = (store: string) => {
    const eventDetails = {
      location: 'how to connect my guitar or bass',
      locationDetail: isProductInStock ? 'tone cable : available' : 'tone cable : not available',
      category: 'download',
      locationPosition: process.env['REACT_APP_PAGE_TYPE'] === 'subscriber' ? 2 : 8,
      action: `rocksmith plus connect app : download on ${store}`,
    };
    sendEvent(eventDetails);
  };

  return (
    <PushContainer
      initial={{
        opacity: 0,
        y: 3,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        type: 'spring',
        delay: 0.2 + index * 0.1,
      }}
      viewport={{ once: true }}
    >
      <PushContent>
        <PushImage>
          <BackgroundImage
            imageNames={[blockImage as string]}
            style={{
              backgroundSize: 'cover',
              backgroundPosition: '50% 50%',
            }}
            dynamic
          />
        </PushImage>
        <PushDetails>
          <PushTitle>
            {blockTitle ? <PushName>{blockTitle}</PushName> : null}
            {blockPrice ? (
              <PushPrice>{blockPrice}</PushPrice>
            ) : blockProductId && !productFound.length ? (
              <div className={'loader'}>
                <Loader backgroundColor={theme.colors['pushBackground']} over={false} />
              </div>
            ) : blockProductId && productPrice ? (
              <PriceContainer>
                {productPrice.discountedPrice ? <SalePill>{sale}</SalePill> : null}
                {productPrice.discountedPrice ? (
                  <DiscountedPrice>{currencyFormatter(productPrice.discountedPrice, locale, currency)}</DiscountedPrice>
                ) : null}
                <BasePrice discount={!!productPrice.discountedPrice}>
                  {currencyFormatter(productPrice.basePrice, locale, currency)}
                </BasePrice>
              </PriceContainer>
            ) : null}
          </PushTitle>
          <PushActions>
            {blockProductId && (
              <Button type={'buy'} handleClick={goToProduct}>
                {getItNow}
              </Button>
            )}
            {blockAppStore?.link ? (
              <a
                href={blockAppStore?.link}
                target={'_blank'}
                rel={'noreferrer'}
                onClick={() => sendAppStoreTracking('app store')}
              >
                <Image imageNames={['buttonAppStore.svg']} altText={blockAppStore.altText as string} dynamic />
              </a>
            ) : null}
            {blockGooglePlay?.link ? (
              <a
                href={blockGooglePlay?.link}
                target={'_blank'}
                rel={'noreferrer'}
                onClick={() => sendAppStoreTracking('google play')}
              >
                <Image imageNames={['buttonGooglePlay.svg']} altText={blockGooglePlay.altText as string} dynamic />
              </a>
            ) : null}
          </PushActions>
        </PushDetails>
      </PushContent>
    </PushContainer>
  );
};

export default withContent(Push);
